/*
 * Copyright 2026 Sony Corporation
 */

import { Alert, Box, Button, Paper, TextField, Typography } from '@mui/material';
import { useState } from 'react';

export interface KnownDistanceProps {
  /** How many of the two points have been clicked. */
  points: number;
  /** Between the two points, in scene metres, once both are in. */
  measured?: number;
  busy: boolean;
  error?: string;
  onApply: (metres: number) => void;
  onRestart: () => void;
  onCancel: () => void;
}

const PROMPTS = ['Click a point on the reconstruction.', 'Click a second point, a known distance from the first.'];

/**
 * The known-distance tool (3d-reconstruction.md §6.4): two clicks and a length.
 *
 * A plain video has no metric source, so this is how a reconstruction learns
 * how big it is. The service applies the result to the whole run.
 */
export const KnownDistance = ({ points, measured, busy, error, onApply, onRestart, onCancel }: KnownDistanceProps) => {
  const [text, setText] = useState('');
  const metres = Number(text);
  const valid = text.trim() !== '' && Number.isFinite(metres) && metres > 0;

  return (
    <Paper
      elevation={3}
      role="dialog"
      aria-label="Set scale from a known distance"
      className="absolute top-3 left-1/2 flex w-96 -translate-x-1/2 flex-col gap-2 p-3"
    >
      <Typography variant="subtitle2">Set scale from a known distance</Typography>
      {measured === undefined ? (
        <Typography variant="body2" color="text.secondary">
          {PROMPTS[Math.min(points, 1)]}
        </Typography>
      ) : (
        <Box
          component="form"
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid && !busy) {
              onApply(metres);
            }
          }}
        >
          <Typography variant="body2" color="text.secondary">
            The points are {measured.toPrecision(3)} m apart at the current scale. How far apart are they really?
          </Typography>
          <TextField
            label="Real distance (m)"
            size="small"
            type="number"
            autoFocus
            value={text}
            onChange={(event) => setText(event.target.value)}
            slotProps={{ htmlInput: { min: 0, step: 'any' } }}
          />
          <Box className="flex justify-end gap-1">
            <Button size="small" onClick={onRestart} disabled={busy}>
              Pick again
            </Button>
            <Button size="small" variant="contained" type="submit" disabled={!valid || busy}>
              Apply
            </Button>
          </Box>
        </Box>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Box className="flex justify-end">
        <Button size="small" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </Box>
    </Paper>
  );
};
